import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { formatInputValue, getNumericInputProps } from './utils/inputHelpers.js';

const SMSFTaxLossHarvesting = ({ smsfState, setSmsfState }) => {
    const [activeTab, setActiveTab] = useState('simple');
    
    // Simple Calculator Inputs (Tests 787-793)
    const [assetName, setAssetName] = useState('');
    const [purchasePrice, setPurchasePrice] = useState(0);
    const [currentValue, setCurrentValue] = useState(0);
    const [purchaseDate, setPurchaseDate] = useState('');
    const [sellDate, setSellDate] = useState('');
    const [capitalGainsThisYear, setCapitalGainsThisYear] = useState(0);
    const [hasCalculated, setHasCalculated] = useState(false);
    
    // Portfolio Tracking
    const [holdings, setHoldings] = useState([]);
    const [showAddForm, setShowAddForm] = useState(false);
    
    const [newHolding, setNewHolding] = useState({
        name: '',
        type: 'australian_shares',
        purchaseDate: '',
        purchasePrice: 0,
        quantity: 0,
        currentPrice: 0,
        unrealizedGainLoss: 0
    });

    // Asset types
    const assetTypes = {
        australian_shares: { name: 'Australian Shares', icon: '🇦🇺' },
        international_shares: { name: 'International Shares', icon: '🌏' },
        us_shares: { name: 'US Shares', icon: '🇺🇸' },
        property: { name: 'Property', icon: '🏠' },
        etf: { name: 'ETFs', icon: '📊' },
        managed_fund: { name: 'Managed Funds', icon: '🏢' }
    };

    // Tax rates
    const TAX_RATES = {
        accumulation: {
            shortTerm: 0.15, // Held < 12 months
            longTerm: 0.10   // Held > 12 months (15% with 1/3 discount)
        },
        pension: {
            all: 0.00        // Tax-free in pension phase
        }
    };

    // Calculate metrics
    const calculateMetrics = () => {
        const totalValue = holdings.reduce((sum, h) => sum + (h.currentPrice * h.quantity), 0);
        const totalCost = holdings.reduce((sum, h) => sum + (h.purchasePrice * h.quantity), 0);
        
        const unrealizedGains = holdings
            .filter(h => h.currentPrice * h.quantity > h.purchasePrice * h.quantity)
            .reduce((sum, h) => sum + ((h.currentPrice - h.purchasePrice) * h.quantity), 0);
        
        const unrealizedLosses = holdings
            .filter(h => h.currentPrice * h.quantity < h.purchasePrice * h.quantity)
            .reduce((sum, h) => sum + ((h.purchasePrice - h.currentPrice) * h.quantity), 0);
        
        const netUnrealized = unrealizedGains - unrealizedLosses;
        
        // Tax on gains if realized
        const potentialTax = unrealizedGains * TAX_RATES.accumulation.longTerm;
        
        // Tax savings from harvesting losses
        const harvestingSavings = unrealizedLosses * TAX_RATES.accumulation.longTerm;
        
        // Count holdings
        const gainHoldings = holdings.filter(h => h.currentPrice > h.purchasePrice).length;
        const lossHoldings = holdings.filter(h => h.currentPrice < h.purchasePrice).length;
        
        return {
            totalValue,
            totalCost,
            unrealizedGains,
            unrealizedLosses,
            netUnrealized,
            potentialTax,
            harvestingSavings,
            count: holdings.length,
            gainHoldings,
            lossHoldings
        };
    };

    const metrics = calculateMetrics();

    // Add holding
    const handleAddHolding = () => {
        if (newHolding.name && newHolding.quantity > 0) {
            const unrealizedGainLoss = (newHolding.currentPrice - newHolding.purchasePrice) * newHolding.quantity;
            setHoldings([...holdings, { ...newHolding, id: Date.now(), unrealizedGainLoss }]);
            setNewHolding({
                name: '',
                type: 'australian_shares',
                purchaseDate: '',
                purchasePrice: 0,
                quantity: 0,
                currentPrice: 0,
                unrealizedGainLoss: 0
            });
            setShowAddForm(false);
        }
    };

    // Calculate holding period
    const calculateHoldingPeriod = (purchaseDate) => {
        if (!purchaseDate) return 0;
        const purchase = new Date(purchaseDate);
        const now = new Date();
        const diffTime = Math.abs(now - purchase);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return Math.floor(diffDays / 365);
    };

    // Simple Calculator Calculation
    const calculateTaxHarvesting = () => {
        setHasCalculated(true);
    };
    
    // Simple calculator results
    const capitalLoss = purchasePrice - currentValue;
    const offsetableGains = capitalGainsThisYear;
    const netCapitalPosition = offsetableGains - capitalLoss;
    const taxSaved = capitalLoss * 0.15; // 15% tax rate on capital gains
    
    // Wash sale check (30 days)
    const daysBetween = purchaseDate && sellDate 
        ? Math.floor((new Date(sellDate) - new Date(purchaseDate)) / (1000 * 60 * 60 * 24))
        : 0;
    const isWashSale = daysBetween > 0 && daysBetween < 30;
    const repurchaseDate = sellDate 
        ? new Date(new Date(sellDate).getTime() + 31 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
        : '';
    
    const renderSimpleCalculator = () => (
        <div className="space-y-6">
            {/* Input Section */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Tax Loss Harvesting Calculator</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Asset Name
                        </label>
                        <input
                            type="text"
                            value={assetName}
                            onChange={(e) => setAssetName(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="BHP Shares"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Purchase Price ($)
                        </label>
                        <input
                            {...getNumericInputProps()}
                            value={purchasePrice}
                            onChange={(e) => setPurchasePrice(Number(e.target.value) || 0)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="50000"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Current Value ($)
                        </label>
                        <input
                            {...getNumericInputProps()}
                            value={currentValue}
                            onChange={(e) => setCurrentValue(Number(e.target.value) || 0)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="42000"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Purchase Date
                        </label>
                        <input
                            type="date"
                            value={purchaseDate}
                            onChange={(e) => setPurchaseDate(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Sell Date
                        </label>
                        <input
                            type="date"
                            value={sellDate}
                            onChange={(e) => setSellDate(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        {isWashSale && (
                            <p className="text-xs text-red-600 mt-1">
                                ⚠️ Warning: Less than 30 days between purchase and sale (wash sale rule)
                            </p>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Capital Gains This Year ($)
                        </label>
                        <input
                            {...getNumericInputProps()}
                            value={capitalGainsThisYear}
                            onChange={(e) => setCapitalGainsThisYear(Number(e.target.value) || 0)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="20000"
                        />
                    </div>
                </div>
                
                <div className="mt-6 flex justify-end">
                    <button
                        onClick={calculateTaxHarvesting}
                        className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
                    >
                        Calculate Tax Impact
                    </button>
                </div>
            </div>
            
            {/* Results Section */}
            {hasCalculated && (
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-semibold mb-4">Tax Harvesting Results</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        {/* Capital Loss */}
                        <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                            <div className="text-sm text-gray-600 mb-1">Capital Loss</div>
                            <div className="text-3xl font-bold text-red-700">
                                -${capitalLoss.toLocaleString()}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                                ${purchasePrice.toLocaleString()} - ${currentValue.toLocaleString()}
                            </div>
                        </div>
                        
                        {/* Offsetable Gains */}
                        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                            <div className="text-sm text-gray-600 mb-1">Offsetable Gains</div>
                            <div className="text-3xl font-bold text-blue-700">
                                ${offsetableGains.toLocaleString()}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">Capital gains this year</div>
                        </div>
                        
                        {/* Net Capital Position */}
                        <div className={`p-4 rounded-lg border ${
                            netCapitalPosition >= 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                        }`}>
                            <div className="text-sm text-gray-600 mb-1">Net Capital Position</div>
                            <div className={`text-3xl font-bold ${
                                netCapitalPosition >= 0 ? 'text-green-700' : 'text-red-700'
                            }`}>
                                ${netCapitalPosition.toLocaleString()}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                                {netCapitalPosition >= 0 ? 'Net gain' : 'Net loss'}
                            </div>
                        </div>
                        
                        {/* Tax Saved */}
                        <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                            <div className="text-sm text-gray-600 mb-1">Tax Saved</div>
                            <div className="text-3xl font-bold text-green-700">
                                ${taxSaved.toLocaleString(undefined, {maximumFractionDigits: 0})}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">15% × ${capitalLoss.toLocaleString()}</div>
                        </div>
                    </div>
                    
                    {/* Wash Sale Warning */}
                    {isWashSale && (
                        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md mb-4">
                            <h4 className="font-semibold text-yellow-800 mb-2">⚠️ Wash Sale Warning</h4>
                            <p className="text-sm text-yellow-700">
                                The asset was held for less than 30 days ({daysBetween} days). 
                                Selling and repurchasing too quickly may trigger wash sale rules.
                            </p>
                        </div>
                    )}
                    
                    {/* Repurchase Timeline */}
                    {sellDate && (
                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
                            <h4 className="font-semibold text-blue-800 mb-2">📅 Repurchase Timeline</h4>
                            <p className="text-sm text-blue-700">
                                <strong>Suggested wait period:</strong> 31+ days after sell date
                            </p>
                            <p className="text-sm text-blue-700 mt-2">
                                You can safely repurchase after: <strong>{repurchaseDate}</strong>
                            </p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );

    // Identify harvest opportunities (for portfolio view)
    const identifyOpportunities = () => {
        const opportunities = [];
        
        // Loss harvesting opportunities
        const lossHoldings = holdings.filter(h => h.currentPrice < h.purchasePrice);
        lossHoldings.forEach(holding => {
            const loss = (holding.purchasePrice - holding.currentPrice) * holding.quantity;
            const taxSaving = loss * 0.15;
            
            opportunities.push({
                type: 'harvest_loss',
                holding: holding.name,
                action: 'Sell to realize loss',
                loss,
                taxSaving,
                priority: loss > 5000 ? 'High' : loss > 1000 ? 'Medium' : 'Low'
            });
        });
        
        return opportunities;
    };
    
    const opportunities = identifyOpportunities();

    const renderOverview = () => (
        <div className="space-y-6">
            {/* Portfolio Summary */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Portfolio Tax Position</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="p-4 bg-blue-50 rounded-lg text-center">
                        <div className="text-2xl font-bold text-blue-600">{metrics.count}</div>
                        <div className="text-sm text-blue-700">Total Holdings</div>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg text-center">
                        <div className="text-2xl font-bold text-green-600">
                            ${metrics.unrealizedGains.toLocaleString()}
                        </div>
                        <div className="text-sm text-green-700">Unrealized Gains</div>
                        <div className="text-xs text-gray-600 mt-1">{metrics.gainHoldings} holdings</div>
                    </div>
                    <div className="p-4 bg-red-50 rounded-lg text-center">
                        <div className="text-2xl font-bold text-red-600">
                            ${metrics.unrealizedLosses.toLocaleString()}
                        </div>
                        <div className="text-sm text-red-700">Unrealized Losses</div>
                        <div className="text-xs text-gray-600 mt-1">{metrics.lossHoldings} holdings</div>
                    </div>
                    <div className={`p-4 rounded-lg text-center ${
                        metrics.netUnrealized >= 0 ? 'bg-green-50' : 'bg-red-50'
                    }`}>
                        <div className={`text-2xl font-bold ${
                            metrics.netUnrealized >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                            ${Math.abs(metrics.netUnrealized).toLocaleString()}
                        </div>
                        <div className={`text-sm ${
                            metrics.netUnrealized >= 0 ? 'text-green-700' : 'text-red-700'
                        }`}>
                            Net Position
                        </div>
                    </div>
                </div>
            </div>

            {/* Tax Impact */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Tax Impact Analysis</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="p-4 bg-red-50 rounded-lg">
                        <h4 className="font-semibold text-red-800 mb-2">If All Gains Realized</h4>
                        <div className="space-y-2">
                            <div className="flex justify-between">
                                <span className="text-sm text-gray-700">Unrealized Gains:</span>
                                <span className="font-medium">${metrics.unrealizedGains.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm text-gray-700">Tax Rate:</span>
                                <span className="font-medium">10%</span>
                            </div>
                            <div className="flex justify-between border-t border-red-200 pt-2">
                                <span className="font-semibold text-gray-700">Potential Tax:</span>
                                <span className="font-bold text-red-600">${metrics.potentialTax.toLocaleString()}</span>
                            </div>
                        </div>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg">
                        <h4 className="font-semibold text-green-800 mb-2">If Losses Harvested</h4>
                        <div className="space-y-2">
                            <div className="flex justify-between">
                                <span className="text-sm text-gray-700">Unrealized Losses:</span>
                                <span className="font-medium">${metrics.unrealizedLosses.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm text-gray-700">Tax Benefit:</span>
                                <span className="font-medium">10%</span>
                            </div>
                            <div className="flex justify-between border-t border-green-200 pt-2">
                                <span className="font-semibold text-gray-700">Tax Savings:</span>
                                <span className="font-bold text-green-600">${metrics.harvestingSavings.toLocaleString()}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Harvest Opportunities */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Tax Harvesting Opportunities</h3>
                {opportunities.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        <p>No tax harvesting opportunities identified</p>
                        <p className="text-sm mt-2">Add holdings to analyze opportunities</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {opportunities.map((opp, index) => (
                            <div key={index} className={`border-l-4 p-4 rounded-r-lg ${
                                opp.priority === 'High' ? 'border-red-500 bg-red-50' :
                                opp.priority === 'Medium' ? 'border-yellow-500 bg-yellow-50' :
                                'border-blue-500 bg-blue-50'
                            }`}>
                                <div className="flex justify-between items-start mb-2">
                                    <div className="flex-1">
                                        <h4 className="font-semibold text-gray-800">{opp.holding}</h4>
                                        <p className="text-sm text-gray-600">{opp.action}</p>
                                        <p className="text-sm text-gray-700 mt-2">
                                            <strong>Strategy:</strong> {opp.strategy}
                                        </p>
                                        <p className="text-sm text-blue-600 mt-1">
                                            <strong>Note:</strong> {opp.consideration}
                                        </p>
                                    </div>
                                    <div className="flex flex-col items-end space-y-2 ml-4">
                                        <span className={`px-2 py-1 text-xs rounded ${
                                            opp.priority === 'High' ? 'bg-red-100 text-red-800' :
                                            opp.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                                            'bg-blue-100 text-blue-800'
                                        }`}>
                                            {opp.priority} Priority
                                        </span>
                                        {opp.taxSaving && (
                                            <div className="text-right">
                                                <div className="text-xs text-gray-600">Tax Saving</div>
                                                <div className="font-bold text-green-600">
                                                    ${opp.taxSaving.toLocaleString()}
                                                </div>
                                            </div>
                                        )}
                                        {opp.potentialSaving && (
                                            <div className="text-right">
                                                <div className="text-xs text-gray-600">Potential Saving</div>
                                                <div className="font-bold text-green-600">
                                                    ${opp.potentialSaving.toLocaleString()}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );

    const renderHoldings = () => (
        <div className="space-y-6">
            {/* Add Holding Button */}
            <div className="flex justify-end">
                <button
                    onClick={() => setShowAddForm(!showAddForm)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                    {showAddForm ? 'Cancel' : '+ Add Holding'}
                </button>
            </div>

            {/* Add Form */}
            {showAddForm && (
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-semibold mb-4">Add Investment Holding</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Asset Name</label>
                            <input
                                type="text"
                                value={newHolding.name}
                                onChange={(e) => setNewHolding({ ...newHolding, name: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                placeholder="e.g., CBA, VAS, Property Address"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Asset Type</label>
                            <select
                                value={newHolding.type}
                                onChange={(e) => setNewHolding({ ...newHolding, type: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            >
                                {Object.entries(assetTypes).map(([key, type]) => (
                                    <option key={key} value={key}>{type.name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Purchase Date</label>
                            <input
                                type="date"
                                value={newHolding.purchaseDate}
                                onChange={(e) => setNewHolding({ ...newHolding, purchaseDate: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Quantity/Units</label>
                            <input
                                {...getNumericInputProps()}
                                value={newHolding.quantity}
                                onChange={(e) => setNewHolding({ ...newHolding, quantity: Number(e.target.value) })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Purchase Price ($)</label>
                            <input
                                {...getNumericInputProps()}
                                step="0.01"
                                value={newHolding.purchasePrice}
                                onChange={(e) => setNewHolding({ ...newHolding, purchasePrice: Number(e.target.value) })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Current Price ($)</label>
                            <input
                                {...getNumericInputProps()}
                                step="0.01"
                                value={newHolding.currentPrice}
                                onChange={(e) => setNewHolding({ ...newHolding, currentPrice: Number(e.target.value) })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>
                    </div>
                    <div className="mt-4 flex justify-end space-x-2">
                        <button
                            onClick={() => setShowAddForm(false)}
                            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleAddHolding}
                            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                        >
                            Add Holding
                        </button>
                    </div>
                </div>
            )}

            {/* Holdings List */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Investment Holdings</h3>
                {holdings.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        <p>No holdings added yet</p>
                        <p className="text-sm mt-2">Click "Add Holding" to start tracking</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-gray-200">
                                    <th className="text-left py-3 px-4">Asset</th>
                                    <th className="text-right py-3 px-4">Quantity</th>
                                    <th className="text-right py-3 px-4">Purchase Price</th>
                                    <th className="text-right py-3 px-4">Current Price</th>
                                    <th className="text-right py-3 px-4">Gain/Loss</th>
                                    <th className="text-right py-3 px-4">Tax Impact</th>
                                    <th className="text-center py-3 px-4">Period</th>
                                    <th className="text-center py-3 px-4">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {holdings.map((holding) => {
                                    const gainLoss = (holding.currentPrice - holding.purchasePrice) * holding.quantity;
                                    const taxImpact = Math.abs(gainLoss) * TAX_RATES.accumulation.longTerm;
                                    const holdingPeriod = calculateHoldingPeriod(holding.purchaseDate);
                                    const isGain = gainLoss > 0;
                                    
                                    return (
                                        <tr key={holding.id} className={`border-b border-gray-100 ${
                                            isGain ? 'bg-green-50' : 'bg-red-50'
                                        }`}>
                                            <td className="py-3 px-4">
                                                <div className="font-medium">{holding.name}</div>
                                                <div className="text-xs text-gray-600">{assetTypes[holding.type].name}</div>
                                            </td>
                                            <td className="py-3 px-4 text-right">{holding.quantity}</td>
                                            <td className="py-3 px-4 text-right">${holding.purchasePrice.toFixed(2)}</td>
                                            <td className="py-3 px-4 text-right">${holding.currentPrice.toFixed(2)}</td>
                                            <td className={`py-3 px-4 text-right font-medium ${
                                                isGain ? 'text-green-600' : 'text-red-600'
                                            }`}>
                                                ${Math.abs(gainLoss).toLocaleString()}
                                                <div className="text-xs">
                                                    {((gainLoss / (holding.purchasePrice * holding.quantity)) * 100).toFixed(1)}%
                                                </div>
                                            </td>
                                            <td className={`py-3 px-4 text-right font-medium ${
                                                isGain ? 'text-red-600' : 'text-green-600'
                                            }`}>
                                                {isGain ? '-' : '+'}${taxImpact.toLocaleString()}
                                            </td>
                                            <td className="py-3 px-4 text-center">
                                                <span className={`px-2 py-1 text-xs rounded ${
                                                    holdingPeriod >= 1 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                                }`}>
                                                    {holdingPeriod >= 1 ? `${holdingPeriod}y` : '< 1y'}
                                                </span>
                                            </td>
                                            <td className="py-3 px-4 text-center">
                                                <button
                                                    onClick={() => setHoldings(holdings.filter(h => h.id !== holding.id))}
                                                    className="text-red-600 hover:text-red-800 text-sm"
                                                >
                                                    Remove
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );

    const renderStrategies = () => (
        <div className="space-y-6">
            {/* Harvesting Strategies */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Tax Loss Harvesting Strategies</h3>
                <div className="space-y-4">
                    {[
                        {
                            strategy: 'Loss Harvesting',
                            description: 'Sell underperforming assets to realize losses',
                            benefit: 'Offset current or future capital gains',
                            timing: 'Before end of financial year',
                            considerations: ['Maintain portfolio exposure', 'Avoid wash sale issues', 'Consider transaction costs']
                        },
                        {
                            strategy: 'Gain Deferral',
                            description: 'Delay selling profitable assets',
                            benefit: 'Defer tax liability to future years',
                            timing: 'Strategic timing based on income',
                            considerations: ['Wait for 12-month CGT discount', 'Consider market conditions', 'Balance portfolio risk']
                        },
                        {
                            strategy: 'Offset Strategy',
                            description: 'Match gains and losses in same financial year',
                            benefit: 'Minimize net tax liability',
                            timing: 'Within same financial year',
                            considerations: ['Track gains already realized', 'Plan sales strategically', 'Monitor year-end deadlines']
                        },
                        {
                            strategy: 'Pension Phase Transition',
                            description: 'Transfer assets to pension before selling',
                            benefit: 'Zero CGT in pension phase',
                            timing: 'At retirement/preservation age',
                            considerations: ['Transfer balance cap limits', 'Pension eligibility', 'Long-term planning']
                        },
                        {
                            strategy: 'Asset Substitution',
                            description: 'Sell losses and buy similar (not identical) assets',
                            benefit: 'Maintain exposure while realizing loss',
                            timing: 'Immediate',
                            considerations: ['30-day wash sale rule', 'Similar but not identical assets', 'Transaction costs']
                        }
                    ].map((item, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4">
                            <h4 className="font-semibold text-gray-800 mb-2">{item.strategy}</h4>
                            <p className="text-sm text-gray-600 mb-2">{item.description}</p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                <div>
                                    <strong className="text-gray-700">Benefit:</strong>
                                    <p className="text-green-700">{item.benefit}</p>
                                </div>
                                <div>
                                    <strong className="text-gray-700">Timing:</strong>
                                    <p className="text-blue-700">{item.timing}</p>
                                </div>
                            </div>
                            <div className="mt-2">
                                <strong className="text-gray-700 text-sm">Considerations:</strong>
                                <ul className="text-sm text-gray-600 mt-1 space-y-1">
                                    {item.considerations.map((consideration, idx) => (
                                        <li key={idx}>• {consideration}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Tax Rules */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Capital Gains Tax Rules</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-blue-50 rounded-lg">
                        <h4 className="font-semibold text-blue-800 mb-2">Accumulation Phase</h4>
                        <ul className="text-sm text-blue-700 space-y-1">
                            <li>• 15% tax on short-term gains (&lt; 12 months)</li>
                            <li>• 10% tax on long-term gains (&gt; 12 months)</li>
                            <li>• 1/3 CGT discount for assets held &gt; 12 months</li>
                            <li>• Losses can offset gains</li>
                            <li>• Losses can be carried forward indefinitely</li>
                        </ul>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg">
                        <h4 className="font-semibold text-green-800 mb-2">Pension Phase</h4>
                        <ul className="text-sm text-green-700 space-y-1">
                            <li>• 0% tax on all capital gains</li>
                            <li>• No distinction between short and long term</li>
                            <li>• Major tax advantage over accumulation</li>
                            <li>• Consider timing of pension commencement</li>
                            <li>• Plan asset sales for pension phase</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="space-y-6 p-6 bg-gray-50 rounded-lg shadow-inner">
            <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-800">Tax Loss Harvesting Tool</h2>
                <div className="flex space-x-2">
                    <button
                        onClick={() => setActiveTab('simple')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeTab === 'simple' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                        }`}
                    >
                        Simple Calculator
                    </button>
                    <button
                        onClick={() => setActiveTab('overview')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeTab === 'overview' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Overview
                    </button>
                    <button
                        onClick={() => setActiveTab('holdings')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeTab === 'holdings' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Holdings
                    </button>
                    <button
                        onClick={() => setActiveTab('strategies')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeTab === 'strategies' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Strategies
                    </button>
                </div>
            </div>

            {activeTab === 'simple' && renderSimpleCalculator()}
            {activeTab === 'overview' && renderOverview()}
            {activeTab === 'holdings' && renderHoldings()}
            {activeTab === 'strategies' && renderStrategies()}

            {/* Action Buttons */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex justify-between items-center">
                    <div className="text-sm text-gray-600">
                        Analysis as of: {new Date().toLocaleDateString()}
                    </div>
                    <div className="flex space-x-4">
                        <button className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors">
                            Export Tax Report
                        </button>
                        <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
                            Generate Action Plan
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

SMSFTaxLossHarvesting.propTypes = {
    smsfState: PropTypes.object.isRequired,
    setSmsfState: PropTypes.func.isRequired
};

export default SMSFTaxLossHarvesting;
